import { useMemo, useState } from 'react'
import type { EstoqueRow } from '../../hooks/useEstoqueAnalytics'
import type { PurchaseOrder } from '../../hooks/useCompras'
import { useConsignado } from '../../hooks/useConsignado'
import { useLocalStorageState } from '../../hooks/useLocalStorageState'
import { useComprasMetasHistory, type MetaDailyRow } from '../../hooks/useComprasMetasHistory'
import { isRupturaAcionavel } from '../../lib/replenishment'
import {
  buildMetas, assembleMetas, computeOtd, contarAtingidas,
  type Faixa, type IndicadorMeta, type MetaValues,
} from '../../lib/comprasMetas'
import { fmtBRL, fmtPct } from '../../lib/format'

interface Props {
  rows: EstoqueRow[]
  orders: PurchaseOrder[]
  /** Índice geral de encalhe (fração) já calculado pelo dashboard — mantém o número consistente. */
  encalheIndex: number | null
  monthNumber: number // 1–12 (define a meta de encalhe)
  monthLabel: string
  monthKey: string // ex.: "2026-05" — chave de persistência das entradas manuais
  todayKey: string // ex.: "2026-06-08" — data de hoje (para snapshots)
  range?: { start: string; end: string }
}

interface ManualEntry { crmNum?: number | null; crmDen?: number | null; degustacoes?: number | null }

const EMPTY_VALUES: MetaValues = { consignado: null, crm: null, ruptura: null, encalhe: null, otd: null, degustacoes: null }

const CARD_TONE: Record<Faixa, string> = {
  verde: 'border-emerald-400/40 bg-emerald-50 dark:bg-emerald-500/10 dark:border-emerald-500/30',
  amarelo: 'border-yellow-400/40 bg-yellow-50 dark:bg-yellow-500/10 dark:border-yellow-500/30',
  vermelho: 'border-red-400/40 bg-red-50 dark:bg-red-500/10 dark:border-red-500/30',
}
const VALUE_TONE: Record<Faixa, string> = {
  verde: 'text-emerald-700 dark:text-emerald-300',
  amarelo: 'text-yellow-700 dark:text-yellow-300',
  vermelho: 'text-red-600 dark:text-red-300',
}
const FAIXA_BADGE: Record<Faixa, string> = {
  verde: 'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/30',
  amarelo: 'bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-500/15 dark:text-yellow-300 dark:border-yellow-500/30',
  vermelho: 'bg-red-100 text-red-600 border-red-300 dark:bg-red-500/15 dark:text-red-300 dark:border-red-500/30',
}
const FAIXA_LABEL: Record<Faixa, string> = { verde: 'Verde', amarelo: 'Amarelo', vermelho: 'Vermelho' }

const numInput = 'w-full min-w-0 bg-paper border border-line rounded px-2 py-1 text-right text-ink text-sm focus:outline-none focus:border-brand-500'

function fmtResultado(m: IndicadorMeta): string {
  if (m.value == null) return '—'
  return m.unidade === 'brl' ? fmtBRL(m.value) : fmtPct(m.value * 100, 1)
}
function parseNum(s: string): number | null {
  if (s.trim() === '') return null
  const n = Number(s.replace(',', '.'))
  return Number.isFinite(n) ? n : null
}
function fmtDateBR(key: string): string {
  const [y, m, d] = key.split('-')
  return `${d}/${m}/${y}`
}
function pickValues(r: MetaDailyRow): MetaValues {
  return { consignado: r.consignado, crm: r.crm, ruptura: r.ruptura, encalhe: r.encalhe, otd: r.otd, degustacoes: r.degustacoes }
}

export default function MetasComprasSection({ rows, orders, encalheIndex, monthNumber, monthLabel, monthKey, todayKey, range }: Props) {
  const { consignadoCount } = useConsignado()
  const history = useComprasMetasHistory()
  const [manualAll, setManualAll] = useLocalStorageState<Record<string, ManualEntry>>('bx:compras-metas', {})
  // null = "ao vivo" (hoje); string YYYY-MM-DD = consulta/edição de uma data salva.
  const [viewDate, setViewDate] = useState<string | null>(null)
  // edições locais não persistidas, por data (persistem ao clicar em "Salvar registro").
  const [draft, setDraft] = useState<Record<string, Partial<MetaValues>>>({})

  const entry = manualAll[monthKey] ?? {}
  const liveCrm = entry.crmNum != null && entry.crmDen != null && entry.crmDen > 0 ? entry.crmNum / entry.crmDen : null
  function patchManual(p: Partial<ManualEntry>) {
    setManualAll(prev => ({ ...prev, [monthKey]: { ...(prev[monthKey] ?? {}), ...p } }))
  }

  // Indicadores ao vivo (modo padrão).
  const liveMetas = useMemo(() => {
    const totalSkus = rows.length
    const rupturaCount = rows.filter(r => isRupturaAcionavel(r.current_stock, r.kanban_status)).length
    const otd = computeOtd(orders, range).pct
    return buildMetas({
      totalSkus, consignadoCount, rupturaCount, encalheIndex, otd,
      crmPct: liveCrm, degustacoes: entry.degustacoes ?? null, month: monthNumber,
    })
  }, [rows, orders, range, encalheIndex, consignadoCount, liveCrm, entry.degustacoes, monthNumber])

  const isHistory = viewDate != null
  const histMonth = viewDate ? Number(viewDate.slice(5, 7)) : monthNumber

  // Valores de uma data consultada: registro salvo no banco OU pré-busca (OTD do mês + CRM/Deg da entrada mensal).
  const histInfo = useMemo(() => {
    if (!viewDate) return { values: EMPTY_VALUES, hasRow: false }
    const row = history.byDate[viewDate]
    if (row) return { values: pickValues(row), hasRow: true }
    const mk = viewDate.slice(0, 7)
    const lastDay = new Date(Number(viewDate.slice(0, 4)), Number(viewDate.slice(5, 7)), 0).getDate()
    const otd = computeOtd(orders, { start: `${mk}-01`, end: `${mk}-${String(lastDay).padStart(2, '0')}` }).pct
    const me = manualAll[mk] ?? {}
    const crm = me.crmNum != null && me.crmDen != null && me.crmDen > 0 ? me.crmNum / me.crmDen : null
    const values: MetaValues = { consignado: null, ruptura: null, encalhe: null, otd, crm, degustacoes: me.degustacoes ?? null }
    return { values, hasRow: false }
  }, [viewDate, history.byDate, orders, manualAll])

  const histValues: MetaValues = isHistory ? { ...histInfo.values, ...(draft[viewDate!] ?? {}) } : EMPTY_VALUES
  const histHasRecord = histInfo.hasRow

  const metas = isHistory ? assembleMetas(histValues, histMonth) : liveMetas
  const { atingidas, avaliadas } = contarAtingidas(metas)

  const savedDates = useMemo(() => Object.keys(history.byDate).sort().reverse(), [history.byDate])

  async function saveTodaySnapshot() {
    await history.saveToday(liveCrm, entry.degustacoes ?? null)
    setViewDate(todayKey)
  }
  async function saveHistRecord() {
    if (!viewDate) return
    const ok = await history.upsertManual(viewDate, histValues)
    if (ok) setDraft(prev => { const n = { ...prev }; delete n[viewDate]; return n })
  }
  async function deleteSnapshot() {
    if (!viewDate) return
    await history.removeDate(viewDate)
    setDraft(prev => { const n = { ...prev }; delete n[viewDate]; return n })
    setViewDate(null)
  }
  function patchHist(id: keyof MetaValues, val: number | null) {
    if (!viewDate) return
    setDraft(prev => ({ ...prev, [viewDate]: { ...(prev[viewDate] ?? {}), [id]: val } }))
  }

  const dirty = isHistory && viewDate != null && (draft[viewDate] != null && Object.keys(draft[viewDate]!).length > 0)
  const periodTitle = isHistory ? fmtDateBR(viewDate!) : `${monthLabel} · ao vivo`

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-faint">Metas do mês · {periodTitle}</h3>
          <p className="text-[11px] text-ink-faint">6 indicadores da Equipe de Compras · meta de encalhe varia por mês (Mai 15% · Jun 13% · Jul 11%)</p>
        </div>
        <div className="text-sm">
          <span className="text-ink-faint">Atingidas: </span>
          <span className="font-bold text-emerald-400">{atingidas}</span>
          <span className="text-ink-faint"> de {avaliadas || 6}</span>
          {avaliadas < 6 && <span className="text-[11px] text-ink-faint"> · {6 - avaliadas} sem dado</span>}
        </div>
      </div>

      {/* ── Barra de histórico ── */}
      <div className="flex flex-wrap items-center gap-2 bg-surface border border-line rounded-lg px-3 py-2">
        <button
          onClick={() => setViewDate(null)}
          className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${!isHistory ? 'bg-brand-600 text-white' : 'bg-sunken text-ink-muted hover:text-ink'}`}
        >
          Ao vivo (hoje)
        </button>
        <label className="flex items-center gap-1.5 text-xs text-ink-muted">
          Consultar data:
          <input
            type="date"
            value={viewDate ?? ''}
            onChange={e => setViewDate(e.target.value || null)}
            className="bg-paper border border-line rounded px-2 py-1 text-ink text-xs focus:outline-none focus:border-brand-500"
          />
        </label>
        {!isHistory ? (
          <button
            onClick={saveTodaySnapshot}
            className="px-2.5 py-1 rounded text-xs font-medium bg-emerald-700 hover:bg-emerald-600 text-white transition-colors"
            title="Calcula e grava os índices de hoje no histórico (compartilhado)"
          >
            📸 Salvar snapshot de hoje ({fmtDateBR(todayKey)})
          </button>
        ) : (
          <>
            <button
              onClick={saveHistRecord}
              className="px-2.5 py-1 rounded text-xs font-medium bg-emerald-700 hover:bg-emerald-600 text-white transition-colors"
              title="Grava os valores desta data no histórico"
            >
              {histHasRecord ? (dirty ? 'Salvar alterações' : 'Salvar registro') : 'Salvar registro desta data'}
            </button>
            {histHasRecord && (
              <button
                onClick={deleteSnapshot}
                className="px-2.5 py-1 rounded text-xs font-medium bg-sunken hover:bg-danger/10 text-ink-muted border border-line transition-colors"
              >
                Excluir registro
              </button>
            )}
          </>
        )}
        {savedDates.length > 0 && (
          <div className="flex items-center gap-1 flex-wrap text-[11px] text-ink-faint">
            <span className="text-ink-faint">Salvos:</span>
            {savedDates.slice(0, 8).map(d => (
              <button
                key={d}
                onClick={() => setViewDate(d)}
                className={`px-1.5 py-0.5 rounded border ${viewDate === d ? 'border-brand-500 text-brand-400' : 'border-line text-ink-muted hover:text-ink'}`}
              >
                {fmtDateBR(d)}
              </button>
            ))}
          </div>
        )}
      </div>

      {isHistory && (
        <p className="text-[11px] text-amber-400/90">
          {histHasRecord
            ? 'Registro salvo no histórico. Ajuste qualquer valor e clique em “Salvar alterações”.'
            : `Sem registro para ${fmtDateBR(viewDate!)}. OTD e CRM/Degustações foram buscados automaticamente para o mês; Consignado, Ruptura e Encalhe não têm histórico de estoque — preencha-os e clique em “Salvar registro desta data”.`}
        </p>
      )}

      <div className="flex flex-wrap gap-2 [&>*]:w-full sm:[&>*]:w-[220px]">
        {metas.map(m => (
          <div
            key={m.id}
            className={`relative flex flex-col gap-1.5 rounded-lg border px-3 py-2 transition-colors ${m.faixa ? CARD_TONE[m.faixa] : 'border-line bg-surface'}`}
          >
            <div className="flex items-start justify-between gap-2">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-ink-muted leading-tight">{m.nome}</p>
              <span
                className={`shrink-0 text-[9px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded ${m.fonte === 'auto' ? 'bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-300' : 'bg-amber-100 text-[#9a5a28] dark:bg-amber-500/15 dark:text-amber-300'}`}
                title={m.fonte === 'auto' ? 'Calculado a partir dos dados do sistema' : 'Sem dado de origem — alimentação manual'}
              >
                {isHistory ? 'registro' : m.fonte}
              </span>
            </div>

            <p className={`text-xl font-bold tabular-nums leading-tight ${m.faixa ? VALUE_TONE[m.faixa] : 'text-ink-muted'}`}>
              {fmtResultado(m)}
            </p>

            {isHistory
              ? <HistoryInput meta={m} onChange={val => patchHist(m.id as keyof MetaValues, val)} />
              : (m.fonte === 'manual' && <ManualInputs id={m.id} entry={entry} patch={patchManual} />)}

            <div className="flex items-center justify-between gap-2 mt-auto pt-1">
              <span className="text-[11px] text-ink-muted">Meta <span className="font-semibold text-ink">{m.metaLabel}</span></span>
              <div className="flex items-center gap-1.5">
                {m.atingiu != null && (
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${m.atingiu ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300' : 'bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-300'}`}>
                    {m.atingiu ? 'ATINGIU' : 'NÃO'}
                  </span>
                )}
                {m.faixa && (
                  <span className={`px-2 py-0.5 rounded text-[10px] font-medium border ${FAIXA_BADGE[m.faixa]}`}>{FAIXA_LABEL[m.faixa]}</span>
                )}
              </div>
            </div>

            <p className="text-[10px] text-ink-faint leading-snug">{m.formula}</p>
          </div>
        ))}
      </div>

      <p className="text-[11px] text-ink-faint">
        No modo <span className="text-sky-600 dark:text-sky-300">ao vivo</span>, Consignado/Ruptura/Encalhe/OTD são calculados do estoque e dos pedidos; CRM e Degustações você preenche. O histórico é gravado automaticamente todo dia (snapshot) e fica compartilhado entre usuários. Use <span className="text-emerald-600 dark:text-emerald-300">Salvar snapshot de hoje</span> para gravar agora. Datas anteriores ao início da captura não têm histórico de estoque — só backfill manual.
      </p>
    </section>
  )
}

/** Entrada única de valor no modo histórico (% ou R$, conforme o indicador). */
function HistoryInput({ meta, onChange }: { meta: IndicadorMeta; onChange: (val: number | null) => void }) {
  const isPct = meta.unidade === 'pct'
  // arredonda só a exibição do input (1 casa em %, 2 em R$) — evita float longo tipo 96,6666…
  const shown = meta.value == null ? '' : (isPct ? Number((meta.value * 100).toFixed(1)) : Number(meta.value.toFixed(2)))
  return (
    <label className="block">
      <span className="block text-[9px] uppercase tracking-wide text-ink-faint mb-0.5">{isPct ? 'Valor (%)' : 'Valor (R$)'}</span>
      <input
        type="number" min={0} step={isPct ? '0.1' : '0.01'} placeholder={isPct ? '0,0' : '0,00'}
        aria-label={`Valor de ${meta.nome}`}
        className={numInput} value={shown}
        onChange={e => {
          const n = parseNum(e.target.value)
          onChange(n == null ? null : (isPct ? n / 100 : n))
        }}
      />
    </label>
  )
}

function ManualInputs({ id, entry, patch }: { id: string; entry: ManualEntry; patch: (p: Partial<ManualEntry>) => void }) {
  if (id === 'crm') {
    return (
      <div className="flex items-end gap-1.5">
        <label className="flex-1 min-w-0">
          <span className="block text-[9px] uppercase tracking-wide text-ink-faint mb-0.5">Em dia</span>
          <input
            type="number" min={0} placeholder="0" aria-label="Fornecedores em dia"
            className={numInput} value={entry.crmNum ?? ''}
            onChange={e => patch({ crmNum: parseNum(e.target.value) })}
          />
        </label>
        <span className="text-ink-faint pb-1.5">/</span>
        <label className="flex-1 min-w-0">
          <span className="block text-[9px] uppercase tracking-wide text-ink-faint mb-0.5">Total ativos</span>
          <input
            type="number" min={0} placeholder="0" aria-label="Total de fornecedores ativos"
            className={numInput} value={entry.crmDen ?? ''}
            onChange={e => patch({ crmDen: parseNum(e.target.value) })}
          />
        </label>
      </div>
    )
  }
  // degustações: valor em R$ no mês
  return (
    <label className="block">
      <span className="block text-[9px] uppercase tracking-wide text-ink-faint mb-0.5">Valor no mês (R$)</span>
      <input
        type="number" min={0} step="0.01" placeholder="0,00" aria-label="Valor de degustações no mês"
        className={numInput} value={entry.degustacoes ?? ''}
        onChange={e => patch({ degustacoes: parseNum(e.target.value) })}
      />
    </label>
  )
}
